import React from 'react';
import { clsx } from 'clsx';

interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export const AuthButton: React.FC<AuthButtonProps> = ({ 
  loading = false,
  variant = 'primary',
  size = 'md',
  children, 
  className, 
  disabled,
  ...props 
}) => {
  // Disable button when loading to prevent double-clicks and multiple API calls
  const isDisabled = disabled || loading;

  return (
    <button
      disabled={isDisabled}
      className={clsx(
        "w-full font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
        {
          // Primary variant
          'bg-primary text-primary-foreground hover:opacity-90': variant === 'primary',
          // Secondary variant
          'bg-card text-foreground border border-border hover:bg-muted': variant === 'secondary',
          // Sizes
          'px-4 py-2 text-xs': size === 'sm',
          'px-6 py-3 text-sm': size === 'md',
          'px-8 py-4 text-base': size === 'lg',
          // Disabled/Loading state
          'opacity-50 cursor-not-allowed': isDisabled,
          'cursor-pointer': !isDisabled,
        },
        className
      )}
      {...props}
    >
      {loading ? (
        <div className="flex items-center justify-center space-x-2">
          {/* Spinning circle to show something is happening */}
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          <span>Loading...</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
};